import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { getBrand } from "../utils/branding";
import {
  Home, Wallet, IdCard, LogOut, Plus, Coins, Sparkles,
  CheckCircle2, Clock, XCircle, Circle,
} from "lucide-react";

import { clearAllTokens } from "../utils/tokenStorage";

const kycMeta = (s) => {
  if (s === "approved") return { label: "APPROVED", Icon: CheckCircle2, color: "var(--au-success)" };
  if (s === "pending") return { label: "PENDING", Icon: Clock, color: "var(--au-warn)" };
  if (s === "rejected") return { label: "REJECTED", Icon: XCircle, color: "var(--au-error)" };
  return { label: "NOT SUBMITTED", Icon: Circle, color: "var(--au-muted)" };
};

export default function ProfileMenu() {
  const brand = getBrand();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const menuRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/api/auth/me")
      .then(r => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const logout = () => {
    if (window.confirm("Logout from your account?")) {
      clearAllTokens();
      // Return to the Aegis landing page (not the login screen)
      window.location.replace("/");
    }
  };

  if (loading) {
    return <div style={{ width: 38, height: 38, borderRadius: "50%",
      background: "var(--au-glass)", border: "1px solid var(--au-border)" }} />;
  }
  if (!user) return null;

  const displayName = user.name || "User";
  const initial = displayName.charAt(0).toUpperCase();
  const productName = (brand.product || brand.name || "").toUpperCase();
  const isAegis = productName.includes("AEGIS");

  return (
    <div ref={menuRef} style={{ position: "relative", zIndex: open ? 3000 : "auto" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: 38, height: 38, borderRadius: "50%",
          background: "var(--au-accent)",
          border: "2px solid var(--au-border)",
          color: "var(--au-btn-text)",
          fontSize: 16, fontWeight: 700,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "var(--au-glow)",
          transition: "transform 0.2s",
        }}
        onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
        onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
        title="Profile">{initial}</button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 12px)", right: 0,
          width: 320, zIndex: 3001,
          maxHeight: "min(82vh, 640px)", overflowY: "auto", overflowX: "hidden",
          // Solid opaque panel — no glass/backdrop-blur so dashboard content
          // behind it never bleeds through.
          background: "var(--au-bg-elevated)",
          backdropFilter: "none", WebkitBackdropFilter: "none",
          border: "1px solid var(--au-border)",
          borderRadius: "var(--au-r-card)",
          boxShadow: "0 20px 55px rgba(0,0,0,0.65)",
        }}>
          {/* Profile Header */}
          <div style={{
            padding: "20px 20px 16px",
            background: "linear-gradient(135deg, var(--au-accent-glow), transparent)",
            borderBottom: "1px solid var(--au-hair)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: "var(--au-accent)",
                color: "var(--au-btn-text)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, fontWeight: 700,
              }}>{initial}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: "var(--au-text)", fontWeight: 700, fontSize: 14,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{displayName}</div>
                <div style={{
                  color: "var(--au-text-2)", fontSize: 11,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{user.email}</div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div style={{ padding: "14px 20px" }}>
            <Row label="USERNAME" value={"@" + user.username} />
            <Row label="ACCOUNT NO" value={user.account_number} mono />
            <Row label="PLAN" value={(user.plan || "free").toUpperCase()} accent />
            <Row label="MEMBER SINCE" value={user.joined_date} />
          </div>

          {/* Credits or Free Tier */}
          {isAegis ? (
            <div style={{
              margin: "0 16px 14px", padding: "14px",
              background: "var(--au-glass-strong)",
              border: "1px solid var(--au-border)",
              borderRadius: "var(--au-r-sm)",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--au-text-2)", letterSpacing: 1, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  <Coins size={14} style={{ color: "var(--au-accent-ink)" }} /> SCAN CREDITS
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--au-accent-ink)" }}>
                  {user.credits}
                </div>
              </div>
              <button
                className="au-pill au-pill-primary au-pill-sm"
                onClick={() => { setOpen(false); nav("/wallet"); }}
                style={{ width: "100%", letterSpacing: 1, boxShadow: "none" }}>
                <Plus size={14} /> Add Credits
              </button>
            </div>
          ) : (
            <div style={{
              margin: "0 16px 14px", padding: "12px 14px",
              background: "var(--au-glass-strong)",
              border: "1px solid var(--au-border)",
              borderRadius: "var(--au-r-sm)", textAlign: "center",
            }}>
              <div style={{ fontSize: 12, color: "var(--au-accent-ink)", fontWeight: 700, letterSpacing: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <Sparkles size={14} /> FREE TIER · UNLIMITED SCANS
              </div>
            </div>
          )}

          {/* Menu Items */}
          <div style={{ borderTop: "1px solid var(--au-hair)", padding: "6px 0" }}>
            <MenuItem icon={Home} label="Dashboard" onClick={() => { setOpen(false); nav("/dashboard"); }} />
            {isAegis && (
              <MenuItem icon={Wallet} label="Wallet" onClick={() => { setOpen(false); nav("/wallet"); }} />
            )}
            <MenuItem icon={LogOut} label="Logout" onClick={logout} danger />
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0" }}>
      <span style={{ fontSize: 10, color: "var(--au-muted)", letterSpacing: 1.5, fontWeight: 600 }}>{label}</span>
      <span
        className={mono ? "au-mono" : undefined}
        style={{
          fontSize: 12,
          color: accent ? "var(--au-accent-ink)" : "var(--au-text)",
          fontWeight: accent ? 700 : 500,
        }}>{value}</span>
    </div>
  );
}

function KycRow({ status }) {
  const { label, Icon, color } = kycMeta(status);
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0" }}>
      <span style={{ fontSize: 10, color: "var(--au-muted)", letterSpacing: 1.5, fontWeight: 600 }}>KYC STATUS</span>
      <span style={{ fontSize: 12, color, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
        <Icon size={13} /> {label}
      </span>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: "100%",
        background: hover ? "var(--au-glass-strong)" : "transparent",
        border: "none", padding: "11px 20px", textAlign: "left",
        color: danger ? "var(--au-error)" : "var(--au-text)",
        fontSize: 13, cursor: "pointer",
        display: "flex", alignItems: "center", gap: 10,
        fontFamily: "var(--au-font)",
        transition: "background 0.15s",
      }}>
      <Icon size={16} style={{ color: danger ? "var(--au-error)" : "var(--au-accent-ink)" }} />
      <span>{label}</span>
    </button>
  );
}
